<template>
  <div>
    <h3 class="mb-3">
      Ready for Onboarding
    </h3>

    <CCard>
      <div
        v-if="isLoading"
        :style="{ height: '20vh' }"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <CSpinner
          class="custom-spinner"
          color="info"
          grow
        />
      </div>

      <div v-else>
        <CCallout color="info">
          <small class="text-muted">Total Ready for Onboarding:</small><br>
          <strong class="h4">
            {{ accountsFormattedAndFiltered.length }}
          </strong>
        </CCallout>

        <CDataTable
          small
          border
          sorter
          column-filter
          :items="accountsFormattedAndFiltered"
          :fields="fields"
          :items-per-page="10"
          :pagination="{ align: 'center' }"
          :sorter-value="{ column: 'registrationDate', asc: false }"
        >
          <template #email-filter>
            <input
              v-model="searchEmail"
              class="form-control form-control-sm"
            >
          </template>

          <template #accountType="{ item }">
            <td>
              <CBadge :color="item.accountType === 'Buyer' ? 'primary' : 'info'">
                {{ item.accountType }}
              </CBadge>
            </td>
          </template>

          <template #registrationDate="{ item }">
            <td>
              {{ item.formattedRegistrationDate }}
            </td>
          </template>

          <template #onboardingStatus="{ item }">
            <td>
              <CBadge :color="getOnboardingStatusBadge(item.onboardingStatus)">
                {{ startCase(item.onboardingStatus) }}
              </CBadge>
            </td>
          </template>

          <template #Actions="{ item }">
            <td>
              <CButton
                color="info"
                size="sm"
                :to="`/dashboard/accounts/${item.accountType.toLowerCase()}/${item.id}`"
                target="_blank"
                class="mr-2 mt-2"
              >
                View
              </CButton>
            </td>
          </template>
        </CDataTable>
      </div>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { startCase } from 'lodash'

import { dateTimeFormat } from '@/utils'

export default {
  name: 'ReadyForOnboarding',

  data() {
    return {
      isLoading: false,
      searchEmail: '',
      fields: [
        { key: 'accountType', label: 'Type', sorter: true },
        { key: 'onboardingStatus', label: 'Onboarding Status' },
        { key: 'name', label: 'Account Name' },
        { key: 'registrationDate', label: 'Registered On' },
        { key: 'ringbaAccountId', label: 'Ringba Account Id', sorter: false },
        { key: 'callTechnology', label: 'Technology' },
        { key: 'country', label: 'Country' },
        { key: 'representativeName', label: 'Representative' },
        { key: 'email', label: 'Email' },
        { key: 'Actions', filter: false }
      ]
    }
  },

  computed: {
    ...mapGetters('accounts', [
      'buyerAccountsReadyForOnboarding',
      'sellerAccountsReadyForOnboarding'
    ]),

    combinedAccounts() {
      return [
        ...this.buyerAccountsReadyForOnboarding.map(acc => ({ ...acc, accountType: 'Buyer' })),
        ...this.sellerAccountsReadyForOnboarding.map(acc => ({ ...acc, accountType: 'Seller' }))
      ]
    },

    accountsFormattedAndFiltered() {
      return this.combinedAccounts.filter(account => {
        if (!this.searchEmail) {
          return true
        }

        return (account.representative?.email?.toLowerCase() || '').includes(this.searchEmail.toLowerCase().trim())
      }).map(acc => {
        const representativeName = [acc.representative?.firstName, acc.representative?.lastName]
          .filter(Boolean)
          .join(' ')
        const registrationDate = acc.registrationDate ? new Date(acc.registrationDate) : null
        const isValidDate = registrationDate && !isNaN(registrationDate.getTime())

        return {
          ...acc,
          email: acc.representative?.email,
          country: acc.companyAddress?.countryCode,
          representativeName,
          formattedRegistrationDate: isValidDate ? dateTimeFormat.format(registrationDate) : ''
        }
      })
    }
  },

  async created() {
    document.title = 'Ready for Onboarding'

    try {
      this.isLoading = true

      await Promise.all([
        this.getPendingBuyerAccounts(),
        this.getPendingSellerAccounts()
      ])
    } catch {
      //
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    startCase,

    ...mapActions('accounts', [
      'getPendingBuyerAccounts',
      'getPendingSellerAccounts'
    ]),

    getOnboardingStatusBadge(status) {
      switch (status) {
        case 'Completed':
          return 'success'

        case 'Pending':
        case 'BankInfoSubmitted':
        case 'PendingDocumentSigning':
        default:
          return 'warning'
      }
    }
  }
}
</script>
